import { RefObject, useEffect } from 'react';

/**
 * Представляет функцию, которая может использоваться для обработки событий щелчка за пределами заданного элемента.
 * @template T - Тип элемента, на который происходит ссылка. По умолчанию - HTMLElement.
 * @param {RefObject<T>} ref - Ссылка на элемент, который должен проверяться на события щелчка.
 * @param {AnyCallback} callback - Функция, которая будет вызвана при возникновении события щелчка за пределами элемента.
 * @returns {void}
 */
export type OutsideClickProps = <T extends HTMLElement = HTMLElement>(ref: RefObject<T>, callback: AnyCallback) => void;

/**
 * Представляет функцию, которая может принимать любое количество аргументов и возвращать void.
 * @param {...any[]} args - Аргументы, передаваемые функции.
 * @returns {void}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCallback = (...args: any[]) => void;

const useClickOutside: OutsideClickProps = (ref, callback) => {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
};

export default useClickOutside;
